import { Card } from '@acceso/design-system';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { GeneralLoading } from '@/components/GeneralStatus/GeneralLoading';
import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { SortDirectionDropdown } from '@/components/SortDirectionDropdown/SortDirectionDropdown';
import { BroadcastScheduleProgramBodySegmentCardHeader } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentCardHeader';
import { BroadcastScheduleProgramBodySegmentCardList } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentCardList';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TSegment } from '@/features/segments/types';
import { useBroadcastTableResponsiveBreakpoint } from '@/hooks/useBroadcastTableResponsiveBreakpoint';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';

type SegmentCardProps = {
  segmentsData: UseInfiniteQueryResult<
    InfiniteData<
      {
        data: TSegment[];
        timestamp: string;
        offset: number;
        total: number;
      },
      unknown
    >,
    Error
  >;
  segments?: boolean;
};

export function SegmentCard({ segmentsData, segments }: SegmentCardProps) {
  const { segmentItems, setVisibleBroadcastProgramCards } = useBroadcastScheduleProgram();
  const { underMinDesktopWith } = useUnderMinDesktopWidth();
  const { underBroadcastTableResponsiveBreakpoint } = useBroadcastTableResponsiveBreakpoint();
  const { t } = useTranslation();

  const { visibleBroadcastProgramCards, pinnedId, toolkitComponents } =
    useBroadcastScheduleProgram();
  const { order_by, setFilters } = useProgramBroadcastFilters();

  const allCardsVisible =
    visibleBroadcastProgramCards.segments &&
    visibleBroadcastProgramCards.segmentDetail &&
    pinnedId &&
    toolkitComponents[pinnedId];

  const sortDirection = order_by?.endsWith('asc') ? 'asc' : 'desc';

  const handleSortDirectionChange = (direction: string) => {
    setFilters({ order_by: `start_datetime ${direction}` });
  };

  return (
    <>
      {segmentsData.isLoading ? (
        <GeneralLoading />
      ) : (
        <Card
          classes={{
            rootSection: clsx(
              '!min-w-full w-full flex-auto order-3 lg:order-1 overflow-auto max-h-[600px] lg:max-h-full',
              {
                'max-w-[22%]': allCardsVisible && underMinDesktopWith,
                'max-w-[25%]': allCardsVisible && !underMinDesktopWith,
              }
            ),
            container: 'py-size-4',
          }}
          id="segments-list"
        >
          <GeneralHeader
            name={t('Franjas del programa')}
            onClose={() => {
              setVisibleBroadcastProgramCards(prev => {
                return {
                  ...prev,
                  segments: !prev.segments,
                };
              });
            }}
          />
          <div className="flex flex-col gap-2">
            <div
              className={clsx(
                'flex justify-between gap-2 pl-2',
                underBroadcastTableResponsiveBreakpoint
                  ? 'flex-col items-start'
                  : 'flex-row items-center'
              )}
            >
              <BroadcastScheduleProgramBodySegmentCardHeader
                className={clsx(underBroadcastTableResponsiveBreakpoint && 'w-full')}
                items={segmentItems}
                total={segmentsData.data?.pages?.[0]?.total || 0}
              />
              <SortDirectionDropdown value={sortDirection} onChange={handleSortDirectionChange} />
            </div>
            <BroadcastScheduleProgramBodySegmentCardList
              items={segmentItems}
              segments={segments}
              segmentsData={segmentsData}
            />
          </div>
        </Card>
      )}
    </>
  );
}
